import { WizardFormData } from '@/sections/customerManagement/components/modals/AddCustomerModal/AddCustomerModal.types';
import { CustomerTypeValue, FlagType, WeekDay } from '@/types';

export const DEFAULT_FORM_DATA: WizardFormData = {
  settings_display_name: '',
  profile_picture: undefined,
  customer_type: null,
  subscription_start_date: '',
  subscription_end_date: '',
  subscription_cost: undefined,
  subscription_msrp: undefined,
  subscription_assets: undefined,
  subscription_applications: undefined,
  intelligence_tier: 'none',
  scan_day: WeekDay.Monday,
  attack_surface: {
    external: false,
    applications: false,
    internal: false,
    cloud: false,
    code: false,
    users: false,
    device: false,
    llm: false,
  },
  plextrac_id: undefined,
  hackerone_program_url: undefined,
  collaborators: [],
  parent_account: undefined,
  allowed_domains: [],
};

export const DEFAULT_FLAGS = [
  FlagType.DISABLE_STATIC_IP_ADDRESSES,
  FlagType.DISABLE_AIMI_TRAINING,
];

export const PILOT_DURATION_BUSINESS_DAYS = 20;

export const MINIMUM_SUBSCRIPTION_COST = 0;

export const SUBSCRIPTION_ELIGIBLE_TYPES = [
  'FREEMIUM',
  'PILOT',
  'ENGAGEMENT',
  'MANAGED',
  'SAAS',
  'BUG_BOUNTY',
] as const;

// Customer types whose scan day is staff-selectable in the wizard; all other
// (locked) tiers have their scan day forced to Monday server-side.
export const EDITABLE_TYPES = ['MANAGED', 'SAAS', 'BUG_BOUNTY'] as const;

export const FREEMIUM_SUBSCRIPTION_CONFIG = {
  estimatedCost: 0,
  msrp: 0,
  selectedTier: 'freemium',
  numberOfAssets: 0,
  numberOfApplications: 0,
  scanSchedule: 'weekly',
} as const;

export const PILOT_SUBSCRIPTION_CONFIG = {
  estimatedCost: 0,
  msrp: 0,
  selectedTier: null,
  numberOfAssets: 0,
  numberOfApplications: 0,
  scanSchedule: 'daily',
} as const;

export const ENGAGEMENT_SUBSCRIPTION_CONFIG = {
  estimatedCost: 0,
  msrp: 0,
  selectedTier: null,
  numberOfAssets: 0,
  numberOfApplications: 0,
  scanSchedule: 'daily',
} as const;

export const MANAGED_SUBSCRIPTION_CONFIG = {
  selectedTier: 'ultimate',
  scanSchedule: 'daily',
} as const;

export const SAAS_SUBSCRIPTION_CONFIG = {
  selectedTier: 'foundational',
  scanSchedule: 'daily',
} as const;

export const BUG_BOUNTY_SUBSCRIPTION_CONFIG = {
  selectedTier: 'ultimate',
  scanSchedule: 'daily',
} as const;

/**
 * Get default form data for a specific customer type.
 * Returns type-aware defaults that include auto-initialized values to prevent
 * false positives in hasUserSetValues() comparison.
 *
 * MANAGED and SAAS types auto-initialize subscription fields, so their defaults
 * include those pre-set values. Other types use base defaults.
 *
 * @param type - Customer type (or null for base defaults)
 * @returns WizardFormData with appropriate defaults for the type
 */
export const getDefaultsForType = (
  type: CustomerTypeValue | null
): WizardFormData => {
  const baseDefaults = { ...DEFAULT_FORM_DATA };

  // MANAGED type: Apply auto-initialized subscription defaults
  if (type === 'MANAGED') {
    return {
      ...baseDefaults,
      customer_type: type,
      subscription_tier: MANAGED_SUBSCRIPTION_CONFIG.selectedTier,
      subscription_scan_schedule: MANAGED_SUBSCRIPTION_CONFIG.scanSchedule,
      subscription_assets: 0,
      subscription_applications: 0,
      intelligence_tier: 'centurion',
    };
  }

  // SAAS type: Apply auto-initialized subscription defaults
  if (type === 'SAAS') {
    return {
      ...baseDefaults,
      customer_type: type,
      subscription_tier: SAAS_SUBSCRIPTION_CONFIG.selectedTier,
      subscription_scan_schedule: SAAS_SUBSCRIPTION_CONFIG.scanSchedule,
      subscription_assets: 0,
      subscription_applications: 0,
      intelligence_tier: 'centurion',
    };
  }

  // BUG_BOUNTY type: Apply auto-initialized subscription defaults (same as MANAGED)
  if (type === 'BUG_BOUNTY') {
    return {
      ...baseDefaults,
      customer_type: type,
      subscription_tier: BUG_BOUNTY_SUBSCRIPTION_CONFIG.selectedTier,
      subscription_scan_schedule: BUG_BOUNTY_SUBSCRIPTION_CONFIG.scanSchedule,
      subscription_assets: 0,
      subscription_applications: 0,
    };
  }

  // All other types (ENGAGEMENT, PILOT, FREEMIUM): Return base defaults with type set
  return {
    ...baseDefaults,
    customer_type: type,
  };
};
